import React from 'react';

import { Divider, Flex, Switch, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconMicrophone, IconPhoto } from '@tabler/icons-react';
import { DroxyBoxedIcon } from 'components/Droxy/DroxyBoxedIcon';
import { AdvancedAccordion } from 'components/SmartAssistantSections/AdvancedSection/AdvancedAccordion';
import { UpgradePlanLine } from 'components/UpgradePlanLine';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { ChatAttachmentConfigurationFormValues } from 'shared/interfaces/SmartAssistant';

interface ChatAttachmentConfigurationAccordionProps {
  chatAttachmentConfigurationForm: UseFormReturnType<ChatAttachmentConfigurationFormValues>;
}

export function ChatAttachmentConfigurationAccordion({
  chatAttachmentConfigurationForm
}: ChatAttachmentConfigurationAccordionProps) {
  const { t } = useTranslation();
  const [subscription] = useSubscriptionContext();

  const isEnabled = subscription.product.enableImageChatAttachment || subscription.product.enableAudioChatAttachment;

  return (
    <AdvancedAccordion
      title={t('SMART_ASSISTANT.ADVANCED_SECTION.ATTACHMENTS_CONFIGURATION.TITLE')}
      description={t('SMART_ASSISTANT.ADVANCED_SECTION.ATTACHMENTS_CONFIGURATION.DESCRIPTION')}
      isEnabled={isEnabled}>
      <Flex direction="column" gap="lg" w="100%">
        <Flex direction="row" w="100%" align="center" gap="md">
          <DroxyBoxedIcon icon={<IconPhoto />} size={40} hasBorder style={{ flexShrink: 0 }} />
          <Flex direction="column" gap={2} style={{ flex: 1 }}>
            <Text size="sm" fw={600} lineClamp={1}>
              {t('SMART_ASSISTANT.ADVANCED_SECTION.ATTACHMENTS_CONFIGURATION.IMAGE.LABEL')}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={2}>
              {t('SMART_ASSISTANT.ADVANCED_SECTION.ATTACHMENTS_CONFIGURATION.IMAGE.DESCRIPTION')}
            </Text>
          </Flex>
          {!subscription.product.enableImageChatAttachment && <UpgradePlanLine />}
          <Switch
            disabled={!subscription.product.enableImageChatAttachment}
            checked={chatAttachmentConfigurationForm.values.isImageEnabled}
            {...chatAttachmentConfigurationForm.getInputProps('isImageEnabled')}
          />
        </Flex>

        <Divider />

        <Flex direction="row" w="100%" align="center" gap="md">
          <DroxyBoxedIcon icon={<IconMicrophone />} size={40} hasBorder style={{ flexShrink: 0 }} />
          <Flex direction="column" gap={2} w="100%">
            <Text size="sm" fw={600} lineClamp={1}>
              {t('SMART_ASSISTANT.ADVANCED_SECTION.ATTACHMENTS_CONFIGURATION.AUDIO.LABEL')}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={2}>
              {t('SMART_ASSISTANT.ADVANCED_SECTION.ATTACHMENTS_CONFIGURATION.AUDIO.DESCRIPTION')}
            </Text>
          </Flex>
          <Switch
            disabled={!subscription.product.enableAudioChatAttachment}
            checked={chatAttachmentConfigurationForm.values.isAudioEnabled}
            {...chatAttachmentConfigurationForm.getInputProps('isAudioEnabled')}
          />
          {!subscription.product.enableAudioChatAttachment && <UpgradePlanLine />}
        </Flex>
      </Flex>
    </AdvancedAccordion>
  );
}
